import { DescService, Message } from '@bufbuild/protobuf';
import {
  Code,
  ConnectError,
  createClient,
  Interceptor,
  makeAnyClient,
  Client,
  Transport,
} from '@connectrpc/connect';
import { createGrpcWebTransport } from '@connectrpc/connect-web';
import * as Sentry from '@sentry/core';

import Cancel from './cancel';
import debugConsole from './debugConsole';
import { BackendDomainError } from './errors';
import { NoResponseError } from './errorsClient';
import { ErrorCode } from './espb/error_codes_pb';
import { DomainErrorSchema } from './espb/errors_pb';
import { trackError } from './promiseUtils';
import { ClientType } from './types';

export type AuthTokens = {
  backend?: string;
  auditlogWrite?: string;
  auditlogAdmin?: string;
  lfdOverridesAdmin?: string;
};

interface EspbServiceClientConfig {
  readonly baseUri: string;
  readonly clientType: ClientType;
  readonly authTokens: AuthTokens;
  readonly clientId?: string;
  readonly clientVersion?: string;
  readonly syncVersion?: number;
  readonly syncSuperUsers: boolean;
  readonly clientOperationId?: string;
  readonly clientOperationName?: string;
}

export type CreateEspbTransport = typeof createGrpcWebTransport;

export default class EspbServiceClientFactory {
  static silent = true;

  private static createTransport = createGrpcWebTransport;

  static setDefaultTransport(createTransport: CreateEspbTransport) {
    EspbServiceClientFactory.createTransport = createTransport;
  }

  private config: EspbServiceClientConfig;

  constructor(config: EspbServiceClientConfig) {
    this.config = {
      ...config,
      baseUri:
        config.baseUri.endsWith('/') ?
          config.baseUri.substring(0, config.baseUri.length - 1)
        : config.baseUri,
    };
  }

  public createServiceClient<T extends DescService>(service: T) {
    const transport = EspbServiceClientFactory.createTransport({
      baseUrl: this.config.baseUri,
      interceptors: [this.createMetadataInterceptor(), this.createLogInterceptor()],
      // Wrap fetch to handle type errors and turn them into ConnectErrors with an error code we can
      // match on down the line. See https://github.com/bufbuild/connect-es/issues/715
      fetch: fetchWithConnectErrors,
    });
    return this.makeDomainErrorPromiseClient<T>(service, transport);
  }

  private makeDomainErrorPromiseClient<T extends DescService>(service: T, transport: Transport) {
    const baseClient = createClient(service, transport);
    return makeAnyClient(service, (method) => {
      const baseClientHandler = baseClient[method.localName];
      if (!baseClientHandler) {
        return null;
      }
      switch (method.methodKind) {
        case 'unary':
          return async function (req, options) {
            try {
              const message = baseClientHandler(req, options) as Promise<Message>;
              return await message;
            } catch (e) {
              domainErrorFromConnectError(e);
            }
          };
        case 'server_streaming':
        case 'client_streaming':
        case 'bidi_streaming':
          return async function* (req, options) {
            const messages = baseClientHandler(req, options) as AsyncIterable<Message>;
            try {
              for await (const message of messages) {
                yield message;
              }
            } catch (e) {
              domainErrorFromConnectError(e);
            }
          };
        default:
          return null;
      }
    }) as Client<T>;
  }

  private createLogInterceptor(): Interceptor {
    return (next) => async (req) => {
      const clientOp = `${this.config.clientOperationName}/${this.config.clientOperationId}`;
      const requestId = `${Math.floor(Math.random() * 10000)}`.padStart(5, '0');
      const methodName = `${req.service.typeName}/${req.method.name}`;
      const shortenedClientId = this.config.clientId?.split('-')[0];

      const log = (...data: any[]) => {
        if (EspbServiceClientFactory.silent) {
          return;
        }
        debugConsole.log(shortenedClientId, clientOp, requestId, methodName, ...data);
      };

      if (!req.stream) {
        // we don't use streaming requests, so we don't implement logging for them
        log('->', req.message);
      }
      const result = await next(req);
      if (result.stream) {
        return {
          ...result,
          message: this.wrapStreamLogger(result.message, log),
        };
      }
      log('<-', result.message);
      return result;
    };
  }

  private async *wrapStreamLogger(messages: AsyncIterable<Message>, log: (...data: any[]) => void) {
    let firstResponseOk = false;
    for await (const message of messages) {
      if (!firstResponseOk) {
        firstResponseOk = true;
        log(`First response`);
      }
      log(`Got stream message`, message);
      yield message;
    }
  }

  private createMetadataInterceptor() {
    const authHeaders: string[] = [];
    if (this.config.authTokens?.backend) {
      authHeaders.push(`backend ${this.config.authTokens.backend}`);
    }
    if (this.config.authTokens?.auditlogWrite) {
      authHeaders.push(`auditlog-write ${this.config.authTokens.auditlogWrite}`);
    }
    if (this.config.authTokens?.auditlogAdmin) {
      authHeaders.push(`auditlog-admin ${this.config.authTokens.auditlogAdmin}`);
    }
    if (this.config.authTokens?.lfdOverridesAdmin) {
      authHeaders.push(`lfd-overrides-admin ${this.config.authTokens.lfdOverridesAdmin}`);
    }

    const metadataInterceptor: Interceptor = (next) => async (req) => {
      const metadata = req.header;
      metadata.set('authorization', authHeaders.join(','));
      if (this.config.clientId) {
        metadata.set('client-id', this.config.clientId);
      }
      if (this.config.clientType) {
        metadata.set('client-type', `${this.config.clientType}`);
      }
      if (this.config.clientVersion) {
        metadata.set('client-version', this.config.clientVersion);
      }
      if (this.config.syncVersion !== undefined) {
        metadata.set('sync-version', `${this.config.syncVersion}`);
      }
      if (this.config.syncSuperUsers) {
        metadata.set('sync-superusers', 'ok');
      }
      if (this.config.clientOperationId) {
        metadata.set('client-operation-id', this.config.clientOperationId);
      }
      if (this.config.clientOperationName) {
        metadata.set('client-operation-name', this.config.clientOperationName);
      }
      return next(req);
    };

    return metadataInterceptor;
  }
}

function domainErrorFromConnectError(reason: unknown): never {
  const error = ConnectError.from(reason);

  Sentry.addBreadcrumb({
    type: 'info',
    category: 'connectError',
    data: {
      reason,
      error,
    },
  });

  if (error.code === Code.Canceled) {
    throw new Cancel();
  }

  const domainErrorDetails = error.findDetails(DomainErrorSchema);
  const espbDomainError = domainErrorDetails.length > 0 && domainErrorDetails[0];
  if (espbDomainError) {
    throw new BackendDomainError(
      error.code,
      espbDomainError.code,
      error.message,
      espbDomainError.userTitle,
      espbDomainError.userDetail,
      espbDomainError.requestId,
    );
  }

  if (error.code === Code.Unavailable || error.code === Code.Aborted) {
    throw new NoResponseError();
  }

  // Unfortunately, there are several scenarios where espb throws an unknown or interal error code,
  // which we can only distinguish by matching the error string.
  if (error.code === Code.Unknown || error.code === Code.Internal) {
    switch (error.rawMessage) {
      case 'network error':
        throw new NoResponseError();
      // These happen in particular when the backend is redeployed and the reverse proxy cuts the
      // stream with a non-protobuf response, which happens on redeployment.
      case 'missing trailer':
        throw new NoResponseError();
      case 'parse binary: cant skip wire type 4':
        throw new NoResponseError();
    }
    if (
      // e.g. read ECONNABORTED
      error.rawMessage.includes('ECONNABORTED') ||
      // e.g. connect ECONNABORTED 111.111.111.111:443
      error.rawMessage.includes('ECONNRESET') ||
      // e.g. connect EHOSTUNREACH 111.111.111.111:443
      error.rawMessage.includes('EHOSTUNREACH') ||
      // e.g. read ENOTCONN
      error.rawMessage.includes('ENOTCONN')
    ) {
      // These are linux TCP connection error states that may happen when a connection is
      // unexpectedly closed, aborted, reset, etc. We get this only while running in nodejs.
      throw new NoResponseError();
    }
  }

  // Similar to above, this error happens when we receive non-protobuf data from the reverse proxy.
  if (error.code === Code.InvalidArgument) {
    if (error.rawMessage.startsWith('parse binary')) {
      throw new NoResponseError();
    }
    if (error.rawMessage.startsWith('unexpected response content type')) {
      throw new NoResponseError();
    }
  }

  throw new BackendDomainError(
    error.code,
    ErrorCode.UNDEFINED,
    error.message,
    'An unexpected error occurred',
    `An unexpected error occurred while communicating with the server: ${error.message}. We are investigating. Please make sure you are using the latest version of our software and try again.`,
    'no-request-id',
  );
}

/*
 * A wrapper around fetch that forces all type errors to Code.Unavailable. Without this, connect
 * incorrectly treats them as unknown errors.
 *
 * This is not a fully accurate error handling, fetch may also throw type errors for quite a few
 * other reasons, but it's the best we can do without string matching browser dependent error
 * messages. In real use all errors thrown here should be related to network conditions.
 *
 * See https://developer.mozilla.org/en-US/docs/Web/API/fetch#exceptions
 */
async function fetchWithConnectErrors(...args: Parameters<typeof fetch>): ReturnType<typeof fetch> {
  // handle errors during the fetch call itself
  let response;
  try {
    response = await fetch(...args);
  } catch (e) {
    if (e instanceof TypeError) {
      throw new ConnectError('Fetch error', Code.Unavailable, undefined, undefined, e);
    }
    throw e;
  }

  if (!response.body) {
    return response;
  }

  // This acquires a lock to the body stream
  const reader = response.body.getReader();

  // Handle errors during the response's body stream. This is needed for stream calls where the body
  // continues after the fetch already has completed
  const wrappedStream = new ReadableStream<Uint8Array>({
    start(controller) {
      async function push() {
        try {
          while (true) {
            // Read from the stream
            const { done, value } = await reader.read();
            // Exit if we're done
            if (done) {
              controller.close();
              return;
            }
            // Else yield the chunk
            controller.enqueue(value);
          }
        } catch (e) {
          if (e instanceof TypeError) {
            controller.error(
              new ConnectError('Fetch error', Code.Unavailable, undefined, undefined, e),
            );
          }
          controller.error(e);
        } finally {
          // Release the lock once we're done with the stream reader
          reader.releaseLock();
        }
      }

      push().catch(trackError);
    },
  });

  const wrappedResponse = new Response(wrappedStream, {
    status: response.status,
    statusText: response.statusText,
    headers: response.headers,
  });
  return wrappedResponse;
}
